import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { ScreenReaderOnly } from 'foundations-components/transitional/components/ScreenReaderOnly';
import { SelectableBox } from 'foundations-components/transitional/components/SelectableBox';
import { Small } from 'foundations-components/transitional/components/Small';
import { StepperInput } from 'foundations-components/transitional/components/StepperInput';
import { PriceDisplay } from 'pricing-pages-ui-components/price/PriceDisplay';
import { useMemo } from 'react';
import debounce from 'react-utils/debounce';
import { RECURRING } from 'self-service-api/constants/ChargeTypes';
import { useCalculatedPrice } from '../../../hooks/useBulkPurchaseOptions';
import { useDisplayedCurrencyCode } from '../../../hooks/useCurrencies';
import { useAppSelector } from '../../../reducers';
import { planBuilderPaymentTermForProductsSelector } from '../../../selectors/PlanBuilderSelectors';
import { billingNameKeySelector, isHubSpotCreditsSkuSelector, packagedQuantityBySkuIdSelector, productForSkuIdSelector, productVersionSelector, unitNameForSkuIdSelector } from '../../../selectors/ProductsSelectors';
import { purchaseMotionsSelector } from '../../../selectors/PurchaseMotionSelectors';
import { AddOnCardPricingFallback } from '../../available-add-ons-list/AddOnCardFallbacks';
import SkeletonLoadWrapper from '../../common/loading/SkeletonLoadWrapper';
import { calculateMaxTouchlessPurchasableQuantity } from '../../../v2/utils/quantityPackUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ExpandableChecklistItem = ({
  skuId,
  selectedQuantity,
  onSelectedChange,
  use
}) => {
  const paymentTerm = useAppSelector(planBuilderPaymentTermForProductsSelector) || 'monthly';
  const product = useAppSelector(state => productForSkuIdSelector(state, {
    skuId
  }));
  const apiName = product === null || product === void 0 ? void 0 : product.apiName;
  const billingNameKey = useAppSelector(state => billingNameKeySelector(state, {
    skuId
  }));
  const currencyCode = useDisplayedCurrencyCode();
  const isRecurring = useAppSelector(state => {
    const {
      chargeType
    } = productForSkuIdSelector(state, {
      skuId
    });
    return chargeType === RECURRING;
  });
  const quantifiable = useAppSelector(state => {
    const productVersion = productVersionSelector(state, {
      skuId
    });
    return !!(productVersion !== null && productVersion !== void 0 && productVersion.quantifiable);
  });
  const {
    monthlyCalculatedPrice = 0,
    oneTimeFeeCalculatedPrice = 0,
    loading: calculatedPriceLoading
  } = useCalculatedPrice({
    apiName,
    queryParamTerm: paymentTerm
  });
  const singleUnitPrice = useMemo(() => {
    if (isRecurring && !calculatedPriceLoading) {
      return monthlyCalculatedPrice * (selectedQuantity || 1);
    } else {
      return oneTimeFeeCalculatedPrice * (selectedQuantity || 1);
    }
  }, [isRecurring, monthlyCalculatedPrice, oneTimeFeeCalculatedPrice, selectedQuantity, calculatedPriceLoading]);
  const isTouchlessEligible = useAppSelector(state => purchaseMotionsSelector(state, {
    location: 'CALCULATOR',
    skuId
  }).includes('BUY_TOUCHLESS'));
  const maxTouchlessPurchasableQuantity = useAppSelector(state => {
    const productVersion = productVersionSelector(state, {
      skuId
    });
    return calculateMaxTouchlessPurchasableQuantity(productVersion === null || productVersion === void 0 ? void 0 : productVersion.maxTouchlessPurchasableQuantity);
  });
  const isHubSpotCreditsProduct = useAppSelector(state => isHubSpotCreditsSkuSelector(state, {
    skuId
  }));
  const unitName = useAppSelector(state => unitNameForSkuIdSelector(state, {
    skuId
  }));
  const packagedQuantities = useAppSelector(state => unitName ? packagedQuantityBySkuIdSelector(state, {
    skuId,
    unitName
  }) : 1);
  const minValue = packagedQuantities;
  const maxValue = packagedQuantities * maxTouchlessPurchasableQuantity;
  const canBuyMultiple = quantifiable && maxValue > 1;
  const checked = selectedQuantity > 0;
  const handleStepperChange = debounce(evt => {
    const newQuantity = Math.round(parseInt(evt.target.value, 10) / packagedQuantities);
    if (isNaN(newQuantity)) return;
    onSelectedChange({
      value: newQuantity,
      skuId,
      apiName,
      previousValue: selectedQuantity
    });
  }, 500);
  return /*#__PURE__*/_jsx(SelectableBox, {
    textAlign: "left",
    type: "checkbox",
    selectionMarkLocation: "top-right",
    selected: checked,
    onSelectedChange: () => {
      onSelectedChange({
        value: checked ? 0 : 1,
        skuId,
        apiName,
        previousValue: selectedQuantity
      });
    },
    "data-selenium-class": `${use}-checklist-item`,
    "data-selected": checked,
    "data-touchless-eligible": isTouchlessEligible,
    "data-api-name": apiName,
    children: /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      gap: "200",
      children: [/*#__PURE__*/_jsxs(Flex, {
        flexDirection: "row",
        gap: "200",
        justifyContent: "space-between",
        inlineSize: "100%",
        children: [/*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          gap: "200",
          children: [/*#__PURE__*/_jsx(Flex, {
            flexDirection: "row",
            justifyContent: "start",
            alignItems: "center",
            gap: "200",
            children: /*#__PURE__*/_jsx(Text, {
              typography: "body-125",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: `pricing-pages-ui.productNamesV2.${billingNameKey}.title`
              })
            })
          }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
            isLoading: calculatedPriceLoading,
            heights: [20],
            widths: ['100%'],
            children: singleUnitPrice !== undefined ? /*#__PURE__*/_jsxs(Text, {
              typography: "body-125",
              children: [/*#__PURE__*/_jsx(PriceDisplay, {
                price: singleUnitPrice,
                currencyCode: currencyCode
              }), isRecurring ? /*#__PURE__*/_jsx(FormattedMessage, {
                message: "planBuilder.priceDisplay.perMonth"
              }) : /*#__PURE__*/_jsx(Text, {
                marginInlineStart: "100",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "pricingPage.notes.oneTimeFeeParentheses"
                })
              })]
            }) : /*#__PURE__*/_jsx(AddOnCardPricingFallback, {})
          }), isHubSpotCreditsProduct && /*#__PURE__*/_jsx(Text, {
            typography: "body-125",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "pricingPage.pages.addOns.cards.hubSpotCredits.subtitle"
            })
          })]
        }), canBuyMultiple && /*#__PURE__*/_jsx(View, {
          marginInlineStart: "400",
          onClick: evt => {
            evt.stopPropagation();
          },
          children: /*#__PURE__*/_jsx(FormControl, {
            label: /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx(ScreenReaderOnly, {
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: `pricing-pages-ui.productNamesV2.${billingNameKey}.title`
                })
              }), /*#__PURE__*/_jsx(FormattedMessage, {
                message: "planBuilder.productPages.quantity"
              })]
            }),
            children: /*#__PURE__*/_jsx(StepperInput, {
              inlineSize: "100px",
              "data-selenium-class": `${use}-stepper-input`,
              stepSize: packagedQuantities,
              onChange: handleStepperChange,
              value: selectedQuantity * packagedQuantities,
              min: minValue,
              max: maxValue
            })
          })
        })]
      }), /*#__PURE__*/_jsx(Small, {
        children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: `pricing-pages-ui.productNamesV2.${billingNameKey}.paragraph`
        })
      })]
    })
  });
};
export default ExpandableChecklistItem;